// Step 5: the checks, the .win that will run, the Quantum ESPRESSO hours (route A) and Run; the job's progress
// (nscf, pw2wannier90.x, the disentanglement and localisation iterations) and the project's runs.
import { Accordion, Alert, Badge, Button, Card, Code, Group, Loader, ScrollArea, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ErrorBox, notifyError } from "../../components/Errors";
import { CancelButton, JobProgress, JobStatusBadge, useKindLabel } from "../../components/Jobs";
import { formatDate, get, type Job, type Limits, type ProjectDetail } from "../../lib/api";
import { Plot } from "../../components/Plot";
import { useLiveJob } from "../../lib/hooks";
import { fmtHours } from "../../lib/qe";
import { convergenceFigure, filesProblems, liveSpreadPoint, recipeProblems, runParams, submitWannier, wannierStatus, type WannierCheck, type WannierMemory,
  type WannierRunItem } from "../../lib/wannier";
import { Issues } from "./RecipeSteps";
import type { Remember } from "./WannierWorkspace";

export function RunStep({ project, mem, remember, check, checking, limits, onBack, onDone }: {
  project: ProjectDetail; mem: WannierMemory; remember: Remember; check?: WannierCheck; checking: boolean; limits: Limits;
  onBack: () => void; onDone: () => void;
}) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["wannier-status"], queryFn: wannierStatus, staleTime: 30_000 });
  const { job } = useLiveJob(mem.job);
  const routeA = mem.source === "dft";
  const problems = routeA ? recipeProblems(mem) : filesProblems(mem);
  const blocked = problems.length > 0 || (routeA && (!check || !check.ok));
  const run = useMutation({
    mutationFn: () => { const { kind, params } = runParams(mem); return submitWannier(project.id, kind, params); },
    onSuccess: (j) => {
      remember({ job: j.id });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["wannier-project-runs", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => notifyError(e),
  });
  const active = job && (job.status === "queued" || job.status === "running");
  useEffect(() => { if (job?.status === "succeeded" && run.isSuccess) onDone(); }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const st = status.data;
  const noServer = st && (mem.source === "model" ? false : !st.available);
  const noRouteA = routeA && (!limits["wannier.qe_run"] || (st && !st.route_a_allowed));
  return (
    <Stack gap="sm" mt="md">
      <Card withBorder p="sm">
        <Table fz="sm" data-testid="run-summary">
          <Table.Tbody>
            {routeA ? (
              <>
                <Table.Tr><Table.Td>Route</Table.Td><Table.Td>A: from the DFT run "{mem.dft_label}" (nscf, pw2wannier90.x, wannier90.x)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>Mesh</Table.Td><Table.Td>{mem.mesh.join(" × ")} = {mem.mesh[0] * mem.mesh[1] * mem.mesh[2]} k-points</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>Wannier functions, bands</Table.Td><Table.Td>{check?.num_wann ?? "…"} from {check?.num_bands ?? "…"} bands</Table.Td></Table.Tr>
                <Table.Tr><Table.Td>Windows</Table.Td><Table.Td>{mem.windows ? `outer ${mem.windows.outer.join(" … ")} eV${mem.windows.frozen
                  ? `, frozen ${mem.windows.frozen.join(" … ")} eV` : ""}` : "none (isolated bands)"}</Table.Td></Table.Tr>
              </>
            ) : mem.source === "files" ? (
              <Table.Tr><Table.Td>Route</Table.Td><Table.Td>B: wannier90.x on your interface files ({mem.spin === "collinear" ? "two spin channels" : mem.spin === "spinor" ? "spinors" : "no spin"}),
                E_F = {mem.efermi} eV</Table.Td></Table.Tr>
            ) : (
              <Table.Tr><Table.Td>Route</Table.Td><Table.Td>C: a model file from your Wannier90 output (no programs run)</Table.Td></Table.Tr>
            )}
            {routeA && st && <Table.Tr><Table.Td>Quantum ESPRESSO hours this month</Table.Td>
              <Table.Td>{fmtHours(st.budget.used)} of {fmtHours(st.budget.hours)} used · queue: {st.queue.running} running, {st.queue.queued} waiting</Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </Card>
      {problems.length > 0 && <Alert color="gray" p="xs"><Text size="xs">{problems.join(" ")}</Text></Alert>}
      {routeA && <Group gap="xs">{checking && <Loader size="xs" />}{check?.ok && <Badge color="green" variant="light">checked</Badge>}</Group>}
      {routeA && <Issues issues={check?.issues} />}
      {noServer && <Alert color="orange" p="xs"><Text size="xs">This server has no Wannier90 worker yet.</Text></Alert>}
      {noRouteA && <Alert color="orange" p="xs" title="Wannier90 from DFT is an Advanced feature">
        <Text size="xs">Your plan runs Wannier90 on your own interface files (route B) and builds models (route C); the DFT steps on the
          server need the Advanced plan ("Request access" when you run).</Text></Alert>}
      {routeA && check?.win && (
        <Accordion variant="contained">
          <Accordion.Item value="win">
            <Accordion.Control><Text size="sm">The .win that will run</Text></Accordion.Control>
            <Accordion.Panel><ScrollArea h={260}><Code block data-testid="win-preview">{check.win}</Code></ScrollArea></Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>Back</Button>
        <Group gap="xs">
          {job && <CancelButton job={job} />}
          <Button onClick={() => run.mutate()} loading={run.isPending} disabled={blocked || !!active || !!noServer} data-testid="run-wannier">
            {mem.source === "model" ? "Build the model" : "Run Wannier90"}
          </Button>
        </Group>
      </Group>
      {job && (
        <Stack gap={4}>
          <Group gap="xs"><JobStatusBadge status={job.status} /><Text size="sm" c="dimmed">{formatDate(job.created_at, true)}</Text></Group>
          <JobProgress job={job} />
          <LiveSpread job={job} />
          {job.error && <ErrorBox error={new Error(job.error)} />}
          {job.status === "succeeded" && <Group justify="flex-end"><Button variant="light" onClick={onDone}>See the results</Button></Group>}
        </Stack>
      )}
      <ProjectRuns project={project} current={mem.job} onShow={(id) => { remember({ job: id }); onDone(); }} />
    </Stack>
  );
}

/** The spread while wannier90.x runs (from the job's progress messages): Ω_I in the disentanglement, Ω in the
 * localisation. */
export function LiveSpread({ job }: { job: Job }) {
  const [pts, setPts] = useState<{ id: string; dis: number[][]; loc: number[][] }>({ id: job.id, dis: [], loc: [] });
  const p = liveSpreadPoint(job.message);
  useEffect(() => {
    if (!p) return;
    setPts((cur) => {
      const base = cur.id === job.id ? cur : { id: job.id, dis: [], loc: [] };
      const list = p.phase === "dis" ? base.dis : base.loc;
      if (list.length && list[list.length - 1][0] >= p.iter) return base;
      const next = [...list, [p.iter, p.value]];
      return p.phase === "dis" ? { ...base, dis: next } : { ...base, loc: next };
    });
  }, [job.id, job.message]); // eslint-disable-line react-hooks/exhaustive-deps
  if (job.status !== "running" || pts.id !== job.id || pts.dis.length + pts.loc.length < 2) return null;
  return <Plot {...convergenceFigure({ disentanglement: pts.dis, localisation: pts.loc }, "Spread while Wannier90 runs")} height={260} />;
}

export function ProjectRuns({ project, current, onShow }: { project: ProjectDetail; current?: string; onShow: (id: string) => void }) {
  const label = useKindLabel();
  const runs = useQuery({ queryKey: ["wannier-project-runs", project.id, project.jobs.map((j) => j.id + j.status).join()],
    queryFn: () => get<WannierRunItem[]>(`/wannier/projects/${project.id}/runs`) });
  if (!runs.data?.length) return null;
  return (
    <Card withBorder mt="xs">
      <Text fw={600} size="sm" mb="xs">Runs in this project</Text>
      <Table fz="sm" data-testid="wannier-runs">
        <Table.Tbody>
          {runs.data.map((r) => {
            const om = r.omega ? Object.values(r.omega)[0] : null;
            return (
              <Table.Tr key={r.job_id}>
                <Table.Td><JobStatusBadge status={r.status as any} /></Table.Td>
                <Table.Td>{label(r.kind)}</Table.Td>
                <Table.Td>{r.num_wann ? `${r.num_wann} functions` : ""}{om != null ? ` · Ω = ${om.toFixed(3)} Å²` : ""}</Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatDate(r.created_at, true)}</Text></Table.Td>
                <Table.Td>{r.status === "succeeded" && <Button size="compact-xs" variant={r.job_id === current ? "light" : "subtle"}
                  onClick={() => onShow(r.job_id)}>{r.job_id === current ? "Shown" : "Show"}</Button>}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
